import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { insertGastoRapido } from '../../lib/cierre/queries';
import { cierreMetodoToGastoEnum } from '../../lib/cierre/metodoMap';
import type { MetodoCierre } from '../../lib/cierre/types';
import { METODOS_CIERRE, LABELS_METODO_CIERRE } from '../../lib/cierre/types';
import type { CategoriaGasto } from '../../lib/gastos/types';
import { LABELS_CATEGORIA } from '../../lib/gastos/types';
import type { EmpresaMonedaContext } from '../../utils/empresaMoneda';

const CATEGORIAS: CategoriaGasto[] = [
  'arriendo',
  'servicios_publicos',
  'nomina',
  'proveedor',
  'transporte',
  'papeleria',
  'mercadeo',
  'impuestos',
  'mantenimiento',
  'otro',
];

interface Props {
  empresaId: string;
  usuarioId?: string | null;
  fecha: string;
  empresaCtx?: EmpresaMonedaContext;
  onSuccess: () => void;
}

export const GastoQuickAddCierre: React.FC<Props> = ({ empresaId, usuarioId, fecha, empresaCtx, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState<MetodoCierre>('efectivo');
  const [categoria, setCategoria] = useState<CategoriaGasto>('otro');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = monto.replace(/\D/g, '');
    const m = parseInt(digits, 10) || 0;
    if (!descripcion.trim() || m <= 0) {
      alert('Descripción y monto válido son obligatorios.');
      return;
    }
    setSaving(true);
    try {
      await insertGastoRapido(supabase, {
        empresaId,
        usuarioId,
        fecha,
        descripcion: descripcion.trim(),
        monto: m,
        metodoPago: cierreMetodoToGastoEnum(metodo),
        categoria,
        empresaCtx,
      });
      setDescripcion('');
      setMonto('');
      setMetodo('efectivo');
      setCategoria('otro');
      setOpen(false);
      onSuccess();
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'No se pudo registrar el gasto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-sm font-medium text-[#1A56DB] hover:underline"
        >
          + Registrar gasto rápido
        </button>
      ) : (
        <form onSubmit={submit} className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">Gasto del día ({fecha})</p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
            <input
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Monto (COP)</label>
              <input
                type="text"
                inputMode="numeric"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="Ej. 50000"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Método de pago</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={metodo}
                onChange={(e) => setMetodo(e.target.value as MetodoCierre)}
              >
                {METODOS_CIERRE.map((m) => (
                  <option key={m} value={m}>
                    {LABELS_METODO_CIERRE[m]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Categoría</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as CategoriaGasto)}
            >
              {CATEGORIAS.map((c) => (
                <option key={c} value={c}>
                  {LABELS_CATEGORIA[c]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-[#1A56DB] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Registrar gasto'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
